import React, { createContext, useContext, useReducer } from 'react';

// Action types
const ACTION_TYPES = {
  SET_LOADING: 'SET_LOADING',
  SET_STUDENTS: 'SET_STUDENTS',
  SET_ACADEMIC_YEARS: 'SET_ACADEMIC_YEARS',
  SET_CLASSES: 'SET_CLASSES',
  SET_ERROR: 'SET_ERROR',
  SET_FILTERS: 'SET_FILTERS',
  ADD_STUDENT: 'ADD_STUDENT',
  UPDATE_STUDENT: 'UPDATE_STUDENT',
  DELETE_STUDENT: 'DELETE_STUDENT',
  SET_SELECTED_STUDENTS: 'SET_SELECTED_STUDENTS'
};

// Initial state
const initialState = {
  academicYears: [],
  selectedAcademicYear: '',
  classes: [],
  students: [],
  loading: true,
  studentsLoading: false,
  error: null,
  isSemesterBased: false,
  semesters: [],
  selectedSemester: '',
  selectedClass: '',
  searchQuery: '',
  statusFilter: '',
  selectedStudentIds: [],
  currentPage: 1,
  itemsPerPage: 10
};

// Reducer
const studentReducer = (state, action) => {
  switch (action.type) {
    case ACTION_TYPES.SET_LOADING:
      return {
        ...state,
        loading: action.payload
      };
    
    case ACTION_TYPES.SET_STUDENTS:
      return {
        ...state,
        students: action.payload,
        studentsLoading: false
      };
    
    case ACTION_TYPES.SET_ACADEMIC_YEARS:
      return {
        ...state,
        academicYears: action.payload
      };
    
    case ACTION_TYPES.SET_CLASSES:
      return {
        ...state,
        classes: action.payload
      };
    
    case ACTION_TYPES.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        loading: false
      };
    
    case ACTION_TYPES.SET_FILTERS:
      return {
        ...state,
        ...action.payload
      };
    
    case ACTION_TYPES.ADD_STUDENT:
      return {
        ...state,
        students: [action.payload, ...state.students]
      };
    
    case ACTION_TYPES.UPDATE_STUDENT:
      return {
        ...state,
        students: state.students.map(s =>
          (s.studentId || s._id) === (action.payload.studentId || action.payload._id)
            ? { ...s, ...action.payload }
            : s
        )
      };
    
    case ACTION_TYPES.DELETE_STUDENT:
      return {
        ...state,
        students: state.students.filter(s =>
          (s.studentId || s._id) !== action.payload
        ),
        selectedStudentIds: state.selectedStudentIds.filter(id => id !== action.payload)
      };
    
    case ACTION_TYPES.SET_SELECTED_STUDENTS:
      return {
        ...state,
        selectedStudentIds: action.payload
      };
    
    default:
      return state;
  }
};

// Create context
const StudentContext = createContext();

// Provider component
export const StudentProvider = ({ children }) => {
  const [state, dispatch] = useReducer(studentReducer, initialState);

  const actions = {
    setLoading: (loading) => dispatch({ type: ACTION_TYPES.SET_LOADING, payload: loading }),
    setStudents: (students) => dispatch({ type: ACTION_TYPES.SET_STUDENTS, payload: students }),
    setAcademicYears: (academicYears) => dispatch({ type: ACTION_TYPES.SET_ACADEMIC_YEARS, payload: academicYears }),
    setClasses: (classes) => dispatch({ type: ACTION_TYPES.SET_CLASSES, payload: classes }),
    setError: (error) => dispatch({ type: ACTION_TYPES.SET_ERROR, payload: error }),
    setFilters: (filters) => dispatch({ type: ACTION_TYPES.SET_FILTERS, payload: filters }),
    addStudent: (student) => dispatch({ type: ACTION_TYPES.ADD_STUDENT, payload: student }),
    updateStudent: (student) => dispatch({ type: ACTION_TYPES.UPDATE_STUDENT, payload: student }),
    deleteStudent: (studentId) => dispatch({ type: ACTION_TYPES.DELETE_STUDENT, payload: studentId }),
    setSelectedStudents: (studentIds) => dispatch({ type: ACTION_TYPES.SET_SELECTED_STUDENTS, payload: studentIds })
  };

  return (
    <StudentContext.Provider value={{ state, actions }}>
      {children}
    </StudentContext.Provider>
  );
};

// Custom hook to use student context
export const useStudentContext = () => {
  const context = useContext(StudentContext);
  if (!context) {
    throw new Error('useStudentContext must be used within a StudentProvider');
  }
  return context;
};